import { Dialog as BaseDialog, Transition } from "@headlessui/react";
import { Fragment, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Icon } from "../Icon";
import type { IconType } from "../Icon";
import { FullPageDialogLeaveTime } from "./dialogConstants";
import { H4 } from "../Header";
import { zLayer } from "../../zIndex";
import { FloatingLayerProvider } from "../../floatingLayer";
import { ImageWithFallback } from "../../utils/imageWithFallback";
import type { ImageFallbackType } from "../../utils/imageFallback";

export const SingleFieldDialogRoot = ({
  active,
  onDismiss,
  size = "small",
  children,
}: {
  active: boolean;
  onDismiss?: () => void;
  size?: "small" | "large";
  children: ReactNode;
}) => {
  return (
    <Transition show={active} as={Fragment}>
      <BaseDialog onClose={() => onDismiss?.()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave={`ease-in duration-${FullPageDialogLeaveTime}`}
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`fixed inset-0 ${zLayer.dialog} bg-black/30`}
            aria-hidden="true"
          />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="translate-y-full"
          enterTo="translate-y-0"
          leave="ease-in duration-200"
          leaveFrom="translate-y-0"
          leaveTo="translate-y-full"
        >
          <div
            className={`fixed inset-0 ${zLayer.dialog} flex items-center justify-center`}
          >
            <div className="flex h-full items-center justify-center">
              <BaseDialog.Panel
                className={twMerge(
                  "flex h-min max-h-[85vh] w-screen flex-col gap-4 overflow-auto rounded-lg bg-white p-4",
                  size === "small" ? "max-w-125" : "max-w-175",
                )}
              >
                <FloatingLayerProvider host="dialog">
                  {children}
                </FloatingLayerProvider>
              </BaseDialog.Panel>
            </div>
          </div>
        </Transition.Child>
      </BaseDialog>
    </Transition>
  );
};

export const SingleFieldDialogImage = ({
  src,
  fallback = "supplier-hero",
}: {
  src: string;
  fallback?: ImageFallbackType;
}) => {
  return (
    <figure className="w-full">
      <ImageWithFallback
        img={src}
        className="max-h-96 w-full rounded-lg object-cover"
        fallback={fallback}
      />
    </figure>
  );
};

export const SingleFieldDialogHeader = ({
  title,
  description,
  icon,
  iconClassName,
}: {
  title: string;
  description: ReactNode;
  icon?: IconType;
  iconClassName?: string;
}) => {
  return (
    <header
      className={twMerge("flex items-center gap-4", !icon && "justify-center")}
    >
      {icon && (
        <figure
          className={twMerge(
            "flex h-12 w-12 flex-none items-center justify-center rounded-lg",
            iconClassName,
          )}
        >
          <Icon size={1} icon={icon} />
        </figure>
      )}

      <div
        className={twMerge(
          "flex flex-col",
          !icon && "items-center text-center",
        )}
      >
        <H4 className={"m-0"}>{title}</H4>
        <div className="text-zinc-500">{description}</div>
      </div>
    </header>
  );
};

export const SingleFieldDialogContent = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return <div className={className}>{children}</div>;
};

export const SingleFieldDialogCTA = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={twMerge(
        "mt-auto flex w-full items-end justify-end space-x-3 pt-4",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const SingleFieldDialog = SingleFieldDialogRoot;
